/* eslint-disable jira/jira-ssr/no-unchecked-globals-usage */

export type HydrationOutcomeStats = {
	preserved: number;
	added: number;
	removed: number;
	preservationRatio: number;
	changeRatio: number;
	removedNodes: { node: Node; parent: Node }[];
};

export type HydrationOutcomeReporter = () => HydrationOutcomeStats;
/**
 * copy of src/packages/platform/testing-library/src/ssr/content-preservation.tsx + some tweaks to aid debugging in the browser
 */
export function captureDocumentState(container: Node): HydrationOutcomeReporter {
	const parents = new WeakMap<Node, Node | null>();
	function collectAllNodes(root: Node): Node[] {
		const out: Node[] = [];
		const walker = document.createTreeWalker(root, NodeFilter.SHOW_ALL);
		const stack: string[] = [];

		while (walker.nextNode()) {
			const node = walker.currentNode;

			// special handling for comments to detect start and end of failed suspense blocks
			if (node.nodeType === Node.COMMENT_NODE) {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const commentContent = (node as Comment).data.trim();
				// Detect start of failed suspense block
				if (commentContent === '$!' || commentContent === '$') {
					stack.push(commentContent);
				}

				// Detect end of failed suspense block
				if (commentContent === '/$') {
					stack.pop();
				}
				// always jump over any comments
				// eslint-disable-next-line no-continue
				continue;
			}

			if (stack.includes('$!')) {
				// we are inside a failed suspense block, skip everything until we reach the end
				// these nodes will be removed by React anyway and we dont want to "focus" on here
				// the goal is to support "normal" hydration preservation
				// eslint-disable-next-line no-continue
				continue;
			}

			// ignore "text" nodes
			if (node.nodeType === Node.TEXT_NODE) {
				// eslint-disable-next-line no-continue
				continue;
			}

			// ignore "template" nodes (part of failed suspense and testing harness)
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'TEMPLATE') {
				// eslint-disable-next-line no-continue
				continue;
			}

			out.push(node);
			parents.set(node, node.parentNode);
		}
		if (stack.length > 0) {
			throw new Error('Mismatched suspense comments, found more start than end');
		}
		return out;
	}

	// Capture pre-hydration snapshot
	const originalNodes = collectAllNodes(container);
	const originalSet = new Set(originalNodes);

	return (): HydrationOutcomeStats => {
		const currentNodes = collectAllNodes(container);
		const currentSet = new Set(currentNodes);

		let preserved = 0;
		const removedNodes: Node[] = [];
		for (const n of originalSet) {
			if (currentSet.has(n)) {
				preserved++;
			} else {
				removedNodes.push(n);
			}
		}

		const addedNodes: Node[] = [];
		for (const n of currentSet) {
			if (!originalSet.has(n)) addedNodes.push(n);
		}

		const original = originalSet.size;
		const removed = removedNodes.length;
		const added = addedNodes.length;

		const topLevelRemovedNodes: { node: Node; parent: Node }[] = removedNodes.flatMap((node) => {
			const parent = parents.get(node) ?? null;
			if (parent && document.contains(parent)) {
				return [{ node, parent }];
			}
			return [];
		});

		return {
			preserved,
			removed,
			added,
			preservationRatio: original ? preserved / original : 0,
			changeRatio: original ? (removed + added) / original : 0,
			removedNodes: topLevelRemovedNodes,
		};
	};
}
